import React from 'react';
import { Info, AlertTriangle, AlertOctagon, Plus } from 'lucide-react';

const THRESHOLDS = {
  INFO: 20,
  WARNING: 40,
  DANGER: 60,
};

const cssAnimation = `
@keyframes conversationDangerPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.85; }
}
`;

export default function ConversationLengthIndicator({ messageCount, onNewSession, type = 'project', compact = false }) {
  if (!messageCount || messageCount < THRESHOLDS.INFO) return null;

  let level, Icon, label, shortLabel;

  if (messageCount >= THRESHOLDS.DANGER) {
    level = 'danger';
    Icon = AlertOctagon;
    label = `Session tres longue (${messageCount} messages) — la qualite du contexte se degrade`;
    shortLabel = `${messageCount} msg`;
  } else if (messageCount >= THRESHOLDS.WARNING) {
    level = 'warning';
    Icon = AlertTriangle;
    label = `Session longue (${messageCount} messages) — pensez a creer une nouvelle session`;
    shortLabel = `${messageCount} msg`;
  } else {
    level = 'info';
    Icon = Info;
    label = `${messageCount} messages dans cette session`;
    shortLabel = `${messageCount} msg`;
  }

  const colors = {
    info: { color: '#22c55e', bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.25)' },
    warning: { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)' },
    danger: { color: '#ef4444', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.35)' },
  };

  const c = colors[level];

  // Mode compact : petit pill inline (pour la barre de pills)
  if (compact) {
    return (
      <>
        <style>{cssAnimation}</style>
        <div
          title={label}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '3px 8px', borderRadius: '12px',
            background: c.bg, border: `1px solid ${c.border}`,
            color: c.color, fontSize: '10px', fontWeight: '600',
            whiteSpace: 'nowrap', flexShrink: 0,
            animation: level === 'danger' ? 'conversationDangerPulse 2s ease-in-out infinite' : 'none',
            cursor: level !== 'info' && onNewSession ? 'pointer' : 'default',
          }}
          onClick={level === 'danger' && onNewSession ? onNewSession : undefined}
        >
          <Icon size={10} style={{ flexShrink: 0 }} />
          {shortLabel}
        </div>
      </>
    );
  }

  // Mode classique (pleine largeur) — gardé pour le chat général
  return (
    <>
      <style>{cssAnimation}</style>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 14px', borderRadius: 10,
        fontSize: 13, fontWeight: 500,
        transition: 'all 0.3s ease', marginBottom: 6,
        background: c.bg, border: `1px solid ${c.border}`, color: c.color,
        animation: level === 'danger' ? 'conversationDangerPulse 2s ease-in-out infinite' : 'none',
      }}>
        <Icon size={15} style={{ flexShrink: 0, opacity: 0.9 }} />
        <span style={{ flex: 1 }}>{label}</span>
        {level === 'danger' && onNewSession && type === 'project' && (
          <button
            onClick={onNewSession}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', border: '1px solid rgba(239,68,68,0.4)',
              background: 'rgba(239,68,68,0.15)', color: '#ef4444',
              transition: 'all 0.2s ease', whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { e.target.style.background = 'rgba(239,68,68,0.25)'; }}
            onMouseLeave={e => { e.target.style.background = 'rgba(239,68,68,0.15)'; }}
          >
            <Plus size={12} />
            Nouvelle session
          </button>
        )}
      </div>
    </>
  );
}
